import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import {
  useGetAddonReturnsQuery,
  useUpdateAddonQuantityMutation,
} from "@/services/facilityApis";
import useTableData from "@/components/ui/table/data-table-logic";
import usePagination from "@/hooks/use-pagination";
import Pagination from "@/components/ui/table/pagination-table";
import TableSearchFilter from "@/components/ui/table/data-table-search";
import { DatePickerWithRange } from "@/components/ui/date-range/date-rangePicker";
import { useToast } from "@/components/ui/use-toast";
import AddonReturnsTable from "./table";
import { formatDate } from "@/lib/helper";
import useDocumentTitle from "@/components/ui/common/document-title";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { PlusIcon } from "lucide-react";

interface AddonSearchCriteria {
  limit: number;
  offset: number;
  sort_order: string;
  sort_key: string;
  start_date?: string;
  end_date?: string;
  search_key?: string;
  [key: string]: string | number | string[] | number[] | undefined | null;
}

const current_date = formatDate(new Date()) ?? "";

const initialValue: AddonSearchCriteria = {
  limit: 10,
  offset: 0,
  start_date: current_date,
  end_date: current_date,
  sort_order: "desc",
  sort_key: "id",
};

export default function AddonReturns() {
  useDocumentTitle("AddOn Returns");

  const orgId = useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;
  const { toast } = useToast();

  const {
    searchCriteria,
    setSearchCriteria,
    query,
    toggleSortOrder,
    inputValue,
    setInputValue,
  } = useTableData<AddonSearchCriteria>(initialValue);

  const { data, isLoading, refetch } = useGetAddonReturnsQuery(
    { query, orgId },
    { skip: !orgId }
  );

  const [updateQuantity] = useUpdateAddonQuantityMutation();

  const [editingRowId, setEditingRowId] = useState<number | null>(null);
  const [editingQuantity, setEditingQuantity] = useState<number>(0);

  const {
    handleLimitChange,
    handleNextPage,
    handlePrevPage,
    handleFirstPage,
    handleLastPage,
    isLastPage,
  } = usePagination({
    totalRecords: data?.total_counts || 0,
    searchCriteria,
    setSearchCriteria,
  });

  const handleSave = async (bookingId: number, addonId: number, quantity: number) => {
    try {
      const response = await updateQuantity({
        booking_id: bookingId,
        addon_id: addonId,
        returned_quantity: quantity,
      }).unwrap();

      toast({
        variant: "success",
        title: response?.detail,
      });
      setEditingRowId(null);
      refetch();
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Failed to update quantity",
        description: error?.data?.message || "Something went wrong",
      });
    }
  };

  const handleDateChange = (value: { start_date: Date | undefined; end_date: Date | undefined }) => {
    setSearchCriteria((prev) => ({
      ...prev,
      start_date: value.start_date ? (formatDate(value.start_date) ?? undefined) : undefined,
      end_date: value.end_date ? (formatDate(value.end_date) ?? undefined) : undefined,
      offset: 0,
    }));
  };

  const handleReset = () => {
    setSearchCriteria(initialValue);
    setInputValue("");
  };

  return (
    <div className="w-full p-5">
      <Card className="pt-4">
        <div className="flex flex-col space-y-4 h-full overflow-hidden">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 px-3">
            <TableSearchFilter
              setSearchCriteria={setSearchCriteria}
              inputValue={inputValue}
              setInputValue={setInputValue}
              placeHolder="Search by Name"
            />
            <div className="flex flex-row lg:flex-row lg:justify-center lg:items-center gap-2">
              <div className="text-sm text-black flex items-center gap-1 h-8">
                <DatePickerWithRange
                  name="booking_date"
                  value={{
                    start_date: searchCriteria.start_date ? new Date(searchCriteria.start_date) : undefined,
                    end_date: searchCriteria.end_date ? new Date(searchCriteria.end_date) : undefined,
                  }}
                  onValueChange={handleDateChange}
                  label="Select date range"
                  className="w-full"
                />
              </div>
              <Button
                variant="outline"
                className="text-sm text-black flex items-center gap-1 h-8 px-2"
                onClick={handleReset}
              >
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="gap-2 flex justify-center items-center">
                        <i className="fa-solid fa-arrows-rotate"></i>
                        Reset
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Reset Filter</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </Button>

            </div>
          </div>

          <div className="flex-1 overflow-hidden px-3">
            <AddonReturnsTable
              data={data?.data || []}
              isLoading={isLoading}
              editingRowId={editingRowId}
              editingQuantity={editingQuantity}
              setEditingRowId={setEditingRowId}
              setEditingQuantity={setEditingQuantity}
              handleSave={handleSave}
              searchCriteria={searchCriteria}
              toggleSortOrder={toggleSortOrder}
            />
          </div>

          <div className="px-3 pb-4">
            <Pagination
              limit={searchCriteria.limit}
              offset={searchCriteria.offset}
              totalItems={data?.total_counts || 0}
              onLimitChange={handleLimitChange}
              onNextPage={handleNextPage}
              onPrevPage={handlePrevPage}
              onFirstPage={handleFirstPage}
              onLastPage={handleLastPage}
              isLastPage={isLastPage}
            />
          </div>
        </div>
      </Card>
    </div>
  );
}
